/**
 * Settings wizard step metadata
 *
 * Single source of truth for the settings-wizard step order, paths, phase
 * grouping (used for the checklist landing page), and which steps are
 * optional. Previously this order was hand-duplicated across
 * settings.store.ts, router/guards.ts, and SettingsLayout.vue.
 */
import { ref } from 'vue';

export type WizardPhase = 'foundations' | 'clinical-data' | 'rules-safety' | 'operational';

export interface WizardStepMeta {
  key: string;
  path: string;
  phase: WizardPhase;
  optional: boolean;
  /** Rough time estimate in minutes, shown on the checklist. */
  estMin: number;
}

export const WIZARD_STEPS: WizardStepMeta[] = [
  { key: 'hospital-config', path: '/app-settings/hospital-config', phase: 'foundations', optional: false, estMin: 5 },
  { key: 'roles-permissions', path: '/app-settings/roles-permissions', phase: 'foundations', optional: false, estMin: 3 },
  { key: 'allergy-management', path: '/app-settings/allergy-management', phase: 'clinical-data', optional: true, estMin: 2 },
  { key: 'milk-type', path: '/app-settings/milk-type', phase: 'clinical-data', optional: true, estMin: 2 },
  { key: 'milk-formula-storage', path: '/app-settings/milk-formula-storage', phase: 'clinical-data', optional: false, estMin: 3 },
  { key: 'expiry-rules', path: '/app-settings/expiry-rules', phase: 'rules-safety', optional: false, estMin: 2 },
  { key: 'approval-compliance', path: '/app-settings/approval-compliance', phase: 'rules-safety', optional: false, estMin: 3 },
  { key: 'printer-config', path: '/app-settings/printer-config', phase: 'operational', optional: true, estMin: 2 },
  { key: 'idp-federation', path: '/app-settings/idp-federation', phase: 'operational', optional: true, estMin: 2 },
  { key: 'interoperability-config', path: '/app-settings/interoperability-config', phase: 'operational', optional: true, estMin: 2 },
  { key: 'localization', path: '/app-settings/localization', phase: 'operational', optional: true, estMin: 2 },
  { key: 'profile-settings', path: '/app-settings/profile-settings', phase: 'operational', optional: true, estMin: 2 },
];

export const WIZARD_STEP_KEYS = WIZARD_STEPS.map((s) => s.key);
export const SETTINGS_STEP_PATHS = WIZARD_STEPS.map((s) => s.path);
export const REQUIRED_STEP_KEYS = WIZARD_STEPS.filter((s) => !s.optional).map((s) => s.key);

/**
 * There's no separate entitlement for interoperability — whether the
 * 'interoperability-config' step exists at all is decided solely by the
 * tenant's live bridgelink_enabled flag (GET /assets). Defaults to false
 * (step hidden) until `router/guards.ts`'s `bridgelinkGuard` resolves it on
 * the first settings-module navigation of the session. A plain module-level
 * ref (not a Pinia store) so this file stays free of store/API imports —
 * it's pulled in by nextStepPath/previousStepPath synchronously from inside
 * every step view's save handler, and by router.ts at app bootstrap.
 */
export const bridgelinkEnabled = ref(false);

/** WIZARD_STEPS, minus 'interoperability-config' when bridgelinkEnabled is false. */
export function visibleSteps(): WizardStepMeta[] {
  return WIZARD_STEPS.filter((s) => s.key !== 'interoperability-config' || bridgelinkEnabled.value);
}

export const SETTINGS_WELCOME_PATH = '/app-settings/welcome';

/** Base path of the stepper page (SettingsConfigureLayout.vue) — see wizardSteps.ts header. */
export const SETTINGS_CONFIGURE_BASE = '/app-settings/configure';

/**
 * The 10 step views are reused, unmodified, at two different route bases: the
 * standalone `/app-settings/<key>` pages (SettingsLayout.vue) and the stepper
 * page's `/app-settings/configure/<key>` pages (SettingsConfigureLayout.vue).
 * Each view's save handler calls `nextStepPath(currentKey)` directly with no
 * way to say which base it's currently under, so this reads the live URL
 * instead — safe here since it's always called synchronously, still on the
 * current step's page, before the resulting push.
 */
function currentStepBase(): string {
  return window.location.pathname.startsWith(SETTINGS_CONFIGURE_BASE)
    ? SETTINGS_CONFIGURE_BASE
    : '/app-settings';
}

/** Positional "next" target after `currentKey` in canonical wizard order. */
export function nextStepPath(currentKey: string): string {
  const steps = visibleSteps();
  const i = steps.findIndex((s) => s.key === currentKey);
  const next = steps[i + 1];
  return next ? `${currentStepBase()}/${next.key}` : SETTINGS_WELCOME_PATH;
}

/**
 * Positional "previous" target before `currentKey`, for the stepper page's
 * own Back-button target — unlike `nextStepPath`, this is only ever called
 * from SettingsConfigureLayout.vue's own code, so it takes an explicit base
 * rather than needing to infer one.
 */
export function previousStepPath(currentKey: string, basePath: string = SETTINGS_CONFIGURE_BASE): string {
  const steps = visibleSteps();
  const i = steps.findIndex((s) => s.key === currentKey);
  const prev = steps[i - 1];
  return prev ? `${basePath}/${prev.key}` : SETTINGS_WELCOME_PATH;
}

/** First step not yet completed, in canonical order; falls back to the first step. */
export function firstIncompleteStep(completedSteps: string[]): WizardStepMeta {
  const steps = visibleSteps();
  return steps.find((s) => !completedSteps.includes(s.key)) ?? steps[0];
}

/**
 * Step display copy, shared by the checklist (SettingsWelcomeView.vue) and the
 * stepper page (SettingsConfigureLayout.vue). A function, not a static object,
 * because the strings depend on the caller's live `t()` — a module-level
 * object would freeze them at first import and stop responding to language
 * switches.
 */
export function getStepLabels(t: (key: string) => string): Record<string, { name: string; desc: string }> {
  return {
    'hospital-config': { name: t('settings.nav.hospitalConfig'), desc: t('settings.welcome.step.hospitalConfig') },
    'roles-permissions': { name: t('settings.dashboard.rolesTitle'), desc: t('settings.welcome.step.rolesPermissions') },
    'allergy-management': { name: t('settings.nav.allergyManagement'), desc: t('settings.welcome.step.allergyManagement') },
    'milk-type': { name: t('feed.milkType'), desc: t('settings.welcome.step.milkType') },
    'milk-formula-storage': { name: t('settings.nav.milkFormulaStorage'), desc: t('settings.welcome.step.milkFormulaStorage') },
    'expiry-rules': { name: t('settings.nav.expiryRules'), desc: t('settings.welcome.step.expiryRules') },
    'approval-compliance': { name: t('settings.nav.approvalCompliance'), desc: t('settings.welcome.step.approvalCompliance') },
    'printer-config': { name: t('settings.nav.printerConfig'), desc: t('settings.welcome.step.printerConfig') },
    localization: { name: t('smartSearch.locale.title'), desc: t('settings.welcome.step.localization') },
    'idp-federation': { name: t('settings.idpFederation.pageTitle'), desc: t('settings.welcome.step.idpFederation') },
    'interoperability-config': { name: t('settings.dashboard.interoperabilityTitle'), desc: t('settings.welcome.step.interoperabilityConfig') },
    'profile-settings': { name: t('settings.nav.profileSettings'), desc: t('settings.welcome.step.profileSettings') },
  };
}

/** Reuse the exact icon names SettingsDashboardView.vue already uses per area. */
export const STEP_ICONS: Record<string, string> = {
  'allergy-management': 'AllergyManagement',
  'milk-type': 'MilkInventory',
  'hospital-config': 'Hospital',
  'expiry-rules': 'DocumentCheck',
  'roles-permissions': 'RolesAndPermissions',
  'approval-compliance': 'Security',
  'milk-formula-storage': 'Snowflake',
  'printer-config': 'PrintLabel',
  'idp-federation': 'IdpFederation',
  'interoperability-config': 'LinkChain',
  localization: 'Localization',
  'profile-settings': 'Patients',
};
